import { useState } from 'react';
import { Loader2, Settings } from 'lucide-react';
import { conversionTypeCaption } from '../../lib/websiteVisitsGraph';
import { useDevWebsiteVisitsChart } from '../../hooks/useDevWebsiteVisitsChart';
import { ByMonthButton } from './crmReportPrimitives';
import WebsiteVisitsLineChart from './WebsiteVisitsLineChart';

/** Website visits chart, read from the website report. */
export default function DevWebsiteVisitsChart({
  onByMonth,
  onSettings,
  revision = 0,
}: {
  onByMonth: () => void;
  onSettings: () => void;
  revision?: number;
}) {
  const report = useDevWebsiteVisitsChart(revision);
  const [mode, setMode] = useState<'visits' | 'unique'>('visits');
  const showConversions = report.types.length > 0;

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="border-b border-dotted border-gray-400 pb-0.5 text-sm font-medium text-gray-600">
          Website visits
        </span>
        <div className="flex items-center gap-1">
          <ByMonthButton onClick={onByMonth} label="Expand Website Visits by month" />
          <button
            type="button"
            onClick={onSettings}
            className="rounded-full border border-gray-200/60 bg-white p-1.5 text-gray-500 transition-colors hover:bg-blue-50 hover:text-blue-600"
            title="Conversion Rate Settings"
          >
            <Settings className="h-4 w-4" />
          </button>
        </div>
      </div>

      {report.loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading website data…
        </div>
      ) : (
        <>
          {report.error ? <p className="mb-2 text-[12px] text-red-700">{report.error}</p> : null}
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setMode('visits')}
              className={`flex items-center gap-1.5 transition-opacity ${
                mode === 'visits' ? 'opacity-100' : 'opacity-40 hover:opacity-70'
              }`}
            >
              <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-blue-500" />
              <span className="text-lg font-bold tabular-nums text-gray-900 sm:text-xl">
                {report.visits.toLocaleString()}
              </span>
              <span className="mb-0.5 self-end text-[10px] text-gray-500">visits</span>
            </button>
            <button
              type="button"
              onClick={() => setMode('unique')}
              className={`flex items-center gap-1.5 transition-opacity ${
                mode === 'unique' ? 'opacity-100' : 'opacity-40 hover:opacity-70'
              }`}
            >
              <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-purple-600" />
              <span className="text-lg font-bold tabular-nums text-gray-900 sm:text-xl">
                {report.users.toLocaleString()}
              </span>
              <span className="mb-0.5 self-end text-[10px] text-gray-500">unique</span>
            </button>
            {report.conversionRate ? (
              <div className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-orange-600" />
                <span className="text-lg font-bold tabular-nums text-gray-900 sm:text-xl">
                  {report.conversionRate}%
                </span>
                <span className="mb-0.5 self-end text-[10px] text-gray-500">conv.</span>
              </div>
            ) : null}
            {report.types.map((type) => (
              <div key={type.label} className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-emerald-600" />
                <span className="text-lg font-bold tabular-nums text-gray-900 sm:text-xl">
                  {type.count.toLocaleString()}
                </span>
                <span className="mb-0.5 self-end text-[10px] text-gray-500">
                  {conversionTypeCaption(type.label, type.count)}
                </span>
              </div>
            ))}
          </div>
          <div className="h-[200px] w-full">
            <WebsiteVisitsLineChart
              days={report.days}
              mode={mode}
              showConversions={showConversions}
              openDay={report.openDay}
            />
          </div>
        </>
      )}
    </div>
  );
}
